import * as Sentry from '@sentry/browser';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import qs from 'qs';

import { minutesToMilliseconds } from '../helpers/dateTimeHelpers';
import { handleAxiosErrors } from '../helpers/errors';

import {
    FormulaAddLabelModel,
    FormulaUpdateBase,
} from '../models/formula.model';

import envConfig from '../services/env-config';

import auth from './auth';

// Base API URL
const API_URL = envConfig.API_URL;
const ELIGIBILITY_API_URL = `${envConfig.COMPOSER_FRONTEND_NEXT_API}/api/eligibility/audiences`;
const DATA_MANAGER_API_URL = `${envConfig.COMPOSER_FRONTEND_NEXT_API}/api/eligibility/audience`;

const API = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    },
    timeout: 60000,
    withCredentials: true,
});

const formUpload = (timeoutDuration: number): AxiosRequestConfig => ({
    timeout: timeoutDuration,
    headers: {
        'Content-Type': 'multipart/form-data',
    },
    withCredentials: true,
});

// Global response interceptor for Sentry logging
API.interceptors.response.use(
    (res) => res,
    (err: AxiosError) => {
        if (err.code === 'ERR_NETWORK' || (err.response?.status ?? 0) >= 500) {
            Sentry.captureException(err);
        }
        return Promise.reject(err);
    },
);

const config = (data?: unknown, timeout?: number): AxiosRequestConfig => ({
    headers: {},
    data,
    timeout,
    withCredentials: true,
});

const extendedTimeout = (duration: number): AxiosRequestConfig => ({
    timeout: duration,
    headers: {},
    withCredentials: true,
});

// Multipart/form-data config
export function formDataConfig(timeout: number): AxiosRequestConfig {
    return {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        timeout,
    };
}

const api_handlers = {
    // Auth & Registration
    login: async (
        email: string,
        password: string,
        cb?: (user: unknown, error: string | null) => void,
    ) => {
        const maxRetries = 5;

        const formData = new FormData();
        formData.append('username', email);
        formData.append('password', password);

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const res = await API.post(`/token`, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'Cache-Control': 'no-cache',
                        Pragma: 'no-cache',
                    },
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const user = (auth as any).setAccessToken(
                    res.data.access_token,
                );
                if (cb) cb(user, null);
                return res;
            } catch (err) {
                const axiosErr = err as AxiosError;
                if (axiosErr.code === 'ERR_NETWORK') {
                    if (attempt === maxRetries) {
                        // If max retries reached, log the error and call the callback with an error message
                        Sentry.captureException(axiosErr);
                        if (cb)
                            cb(
                                null,
                                'Network Error: Unable to reach the server',
                            );
                    } else {
                        // retry on network errors
                        const delay = 1000 * attempt; // progressive backoff
                        await new Promise((resolve) =>
                            setTimeout(resolve, delay),
                        );
                        continue;
                    }
                } else {
                    Sentry.captureException(axiosErr);
                    if (cb)
                        cb(
                            null,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            (axiosErr?.response?.data as any)?.detail
                                ? (axiosErr.response!.data as any).detail
                                : 'Something went wrong',
                        );
                }
            }
        }
    },

    logout: async () => {
        const csrfRes = await fetch(`${envConfig.AUTH_SERVICE}/csrf`, {
            credentials: 'include',
        });
        const { csrfToken } = await csrfRes.json();
        const redirectUrl =
            window.location.protocol +
            '//' +
            window.location.host +
            envConfig.BASE_PATH;
        const callbackUrl = `/sign-in?callbackUrl=${encodeURIComponent(
            redirectUrl,
        )}`;

        // build and submit signout form
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = `${envConfig.AUTH_SERVICE}/signout`;

        form.innerHTML = `
        <input type="hidden" name="csrfToken" value="${csrfToken}" />
        <input type="hidden" name="callbackUrl" value="${callbackUrl}" />
        `;

        document.body.appendChild(form);
        form.submit();
    },

    getDocsAuthToken: () => API.post(`/docs-token`, {}, config()),
    createInviteExistingCustomer: (data: unknown) =>
        API.post(`/invites`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createComposerApiKeys: (data: unknown) =>
        API.post(`/api-keys`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createCustomerLlmContext: (data: unknown) =>
        API.post(`/customer/llm-context`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    revokeComposerApiKey: (id: string | number) =>
        API.post(`/api-keys/${id}/revoke`, undefined, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteComposerApiKey: (id: string | number) =>
        API.delete(`/api-keys/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    checkInviteCode: (code: string) =>
        API.get(`/invites/${code}`, {
            ...config(),
            skipAuth: true,
        } as AxiosRequestConfig).catch((err) => handleAxiosErrors(err)),
    getInviteCodes: () =>
        API.get(`/invites`, config()).catch((err) => handleAxiosErrors(err)),
    createUserFromInvite: (data: unknown) =>
        API.post(`/users`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateUserRole: (userId: string | number, data: unknown) =>
        API.put(`/users/${userId}/role`, data, config()),
    getUsersWithEntityPermissions: (
        entityType: string,
        entityId: string | number,
    ) =>
        API.get(`/users/${entityType}/entity/${entityId}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    forgotPassword: (data: unknown) => {
        return API.post(`/password`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        );
    },
    resetPassword: (data: unknown) => {
        return API.post(`/password/reset`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        );
    },
    getCustomerIndustries: () =>
        API.get(`/customer/industries`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getTimingSlots: () =>
        API.get(`/customer/timing-slots`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getFeatureFlagConfig: () =>
        API.get(`/customer/feature-flag-config`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createCustomer: (data: unknown) =>
        API.post(`/customer`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // users
    getUsers: (params: unknown) =>
        API.get(`/users`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    // userStatus
    getUserStatus: () =>
        API.get(`/users/status`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Support
    sendSupportEmail: (data: unknown) => {
        return API.post(`/support/request`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        );
    },
    // Variants
    createVariant: (data: unknown) =>
        API.post(`/variants`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateVariantType: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/variants/${id}/variant-type`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    // Live Messages
    getLiveMessages: (params: unknown) =>
        API.get(`/metrics/live-messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLiveMessagesV1: (params: unknown) =>
        API.get(`/v1/metrics/live-messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLiveMessagesGraphMetrics: (params: unknown) =>
        API.get(`/metrics/live-messages/graph`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    getRealTimeMessagesMetrics: (params: unknown) =>
        API.get(`/metrics/real-time-messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getRealTimeMessagesMetricsV1: (params: unknown) =>
        API.get(`/v1/metrics/real-time-messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLiveMessagesLabels: (formulaId: string | number, params: unknown) =>
        API.get(`/v1/metrics/live-messages/${formulaId}/labels`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLiveMessagesVariants: (
        formulaId: string | number,
        labelId: string | number,
        params: unknown,
    ) =>
        API.get(`/v1/metrics/live-messages/${formulaId}/labels/${labelId}`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    // Messages
    generateMessages: (formulaId: string | number, params: unknown) =>
        API.get(`/formulas/${formulaId}/preview`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            ...extendedTimeout(200000),
        }).catch((err) => handleAxiosErrors(err)),
    generateMessagesV1: (
        formulaId: string | number,
        data: unknown,
        params: unknown,
    ) =>
        API.post(`/v1/formulas/${formulaId}/preview`, data, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            ...extendedTimeout(30000),
        }).catch((err) => handleAxiosErrors(err)),
    updateFormulaMessageCount: (formulaId: string | number) =>
        API.post(
            `/formulas/${formulaId}/update-message-count`,
            {},
            extendedTimeout(200000),
        ).catch((err) => handleAxiosErrors(err)),
    fetchMessages: (params: unknown) =>
        API.get(`/messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    toggleMessageBoost: (messageId: string | number) =>
        API.post(`/messages/${messageId}/boost`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    toggleMessageActive: (messageId: string | number) =>
        API.post(`/messages/${messageId}/active`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkBoost: (data: unknown) =>
        API.post(`/messages/boost`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkActive: (data: unknown) =>
        API.post(`/messages/active`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Formula
    createFormula: (data: unknown) =>
        API.post(`/formulas`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchFormula: (formulaId: string | number) =>
        API.get(`/formulas/${formulaId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchFormulas: (params?: unknown) =>
        API.get(`/formulas`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    fetchFormulasByCategory: (category: string, params: unknown) =>
        API.get(`/formulas/category/${category}`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    updateFormula: (data: FormulaUpdateBase) =>
        API.post(
            `/formulas/${data.id}/update`,
            data,
            extendedTimeout(20000),
        ).catch((err) => handleAxiosErrors(err)),
    updateBulkFormula: (data: unknown) =>
        API.post(`/formulas/bulk-update`, data, extendedTimeout(20000)).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateFormulaAudience: (
        formula_id: string | number | undefined,
        data: unknown,
    ) =>
        API.post(`/formulas/${formula_id}/audience`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    approveFormula: (id: string | number) =>
        API.post(`/formulas/${id}/approve`, {}, extendedTimeout(20000)).catch(
            (err) => handleAxiosErrors(err),
        ),
    fetchApprovedFormulaV1: (formulaId: string | number, params: unknown) =>
        API.get(`/formulas/${formulaId}/approved`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    archiveFormula: (id: string | number) =>
        API.post(`/formulas/${id}/archive`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    draftFormula: (id: string | number, data: unknown = {}) =>
        API.post(`/formulas/${id}/draft`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    cloneFormula: ({
        formulaId,
        data,
    }: {
        formulaId: string | number;
        data: unknown;
    }) =>
        API.post(
            `/formulas/${formulaId}/clone`,
            data,
            extendedTimeout(200000),
        ).catch((err) => handleAxiosErrors(err)),
    reviewFormula: (id: string | number) =>
        API.post(`/formulas/${id}/review`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteFormula: (id: string | number) =>
        API.delete(`/formulas/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateDatasetFilters: ({
        formulaId,
        data,
    }: {
        formulaId: string | number;
        data: unknown;
    }) =>
        API.post(
            `/formulas/${formulaId}/dataset-filters`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateFormulaTriggers: ({
        formulaId,
        data,
    }: {
        formulaId: string | number;
        data: unknown;
    }) =>
        API.post(`/formulas/${formulaId}/triggers`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateFormulaCompoundTrigger: ({
        formulaId,
        data,
    }: {
        formulaId: string | number | undefined;
        data: unknown;
    }) =>
        API.post(
            `/formulas/${formulaId}/compound-trigger`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateFormulaFilterAttributes: ({
        formulaId,
        data,
    }: {
        formulaId: string | number | undefined;
        data: unknown;
    }) =>
        API.post(
            `/formulas/${formulaId}/filter-attributes`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateFormulaCMSFilters: (
        formulaId: string | number | undefined,
        data: unknown,
    ) =>
        API.post(
            `/formulas/${formulaId}/cms-placeholder-filters`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateFormulaTags: ({
        formulaId,
        data,
    }: {
        formulaId: string | number | undefined;
        data: unknown;
    }) =>
        API.post(`/formulas/${formulaId}/tags`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateFormulaTimeSlots: ({
        formulaId,
        data,
    }: {
        formulaId: string | number | undefined;
        data: unknown;
    }) =>
        API.post(`/formulas/${formulaId}/time-slots`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    addFormulaLabels: (data: FormulaAddLabelModel) =>
        API.post(`/formulas/${data.id}/labels`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteFormulaLabels: ({
        id,
        data,
    }: {
        id: string | number;
        data: unknown;
    }) =>
        API.delete(`/formulas/${id}/labels`, config(data)).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchFormulasCategoryCount: (params: unknown) =>
        API.get(`/formulas/category-count`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    fetchLabelAlternateCount: (formula_id: string | number) =>
        API.get(
            `/formulas/${formula_id}/labels_alternate_count`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    fetchCMSPlaceholder: () =>
        API.get(`/cms-placeholders`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchQuickFillFormulas: () =>
        API.get(`/formulas/quick-fills`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    sendFormulaTestMessage: (formulaId: string | number, data: unknown) =>
        API.post(
            `/v1/formulas/${formulaId}/test-message`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    // Alternates
    createVariantV1: (data: unknown) =>
        API.post(`/v1/alternates`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createVariantBulk: (data: unknown) =>
        API.post(`/variants/create/bulk`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateVariantBulk: (data: unknown) =>
        API.patch(`/variants/update/bulk`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchVariantsByComponentId: (componentId: string | number) =>
        API.get(`/components/${componentId}/variants`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchVariantsByMessageContentId: (messageContentId: string | number) =>
        API.get(
            `/message-contents/${messageContentId}/variants`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateVariant: (variantId: string | number, data: unknown) =>
        API.patch(`/variants/${variantId}/update`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    deleteVariant: (id: string | number) =>
        API.delete(`/variants/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkDeleteVariantByComponentId: (id: string | number, data: unknown) =>
        API.post(
            `/components/${id}/variants/bulk-delete`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    // Datasets
    fetchDatasets: () =>
        API.get(`/datasets`, config()).catch((err) => handleAxiosErrors(err)),
    fetchDatasetById: (datasetId: string | number | undefined) =>
        API.get(`/datasets/${datasetId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchDatasetContentById: (datasetId: string | number) =>
        API.get(`/datasets/${datasetId}/content`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchDatasetFormulasById: (datasetId: string | number) =>
        API.get(`/datasets/${datasetId}/formulas`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchFilteredDatasetContentById: (
        datasetId: string | number,
        filters: string,
    ) =>
        API.get(
            `/datasets/${datasetId}/content?filters=${filters}`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    createDataset: (formData: FormData) =>
        API.post(`/datasets`, formData, formUpload(30000)).catch((err) =>
            handleAxiosErrors(err),
        ),
    uploadDatasetContents: ({
        datasetId,
        formData,
    }: {
        datasetId: string | number;
        formData: FormData;
    }) =>
        API.post(
            `/datasets/${datasetId}/content`,
            formData,
            formUpload(30000),
        ).catch((err) => handleAxiosErrors(err)),
    updateDatasetRowField: ({
        datasetId,
        rowId,
        fieldId,
        data,
    }: {
        datasetId: string | number;
        rowId: string | number;
        fieldId: string | number;
        data: unknown;
    }) =>
        API.post(
            `/datasets/${datasetId}/row/${rowId}/field/${fieldId}/update`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    updateDataset: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(
            `/datasets/${datasetId}/update`,
            data,
            formUpload(30000),
        ).catch((err) => handleAxiosErrors(err)),
    deleteDatasetRows: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(`/datasets/${datasetId}/rows/delete`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    deleteDatasetRowsV1: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(`/v1/datasets/${datasetId}/rows/delete`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    createDatasetRow: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(`/datasets/${datasetId}/row`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createDatasetColumn: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(`/datasets/${datasetId}/column`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkUpdateDatasetCellLabel: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.put(`/datasets/${datasetId}/labels/bulk`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateDatasetCellLabel: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.put(`/datasets/${datasetId}/labels`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchDatasetColumnComponentTypes: (datasetId: string | number) =>
        API.get(`/datasets/${datasetId}/component-types`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    createDatasetColumnComponentType: ({
        datasetId,
        data,
    }: {
        datasetId: string | number;
        data: unknown;
    }) =>
        API.post(
            `/datasets/${datasetId}/component-types`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    deleteDatasetColumnComponentType: ({
        datasetId,
        datasetColumn,
        params,
    }: {
        datasetId: string | number;
        datasetColumn: string | number;
        params: unknown;
    }) =>
        API.delete(`/datasets/${datasetId}/component-types/${datasetColumn}`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    fetchDatasetLabelColumns: (datasetId: string | number) =>
        API.get(`/datasets/${datasetId}/label-columns`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchDatasetRelations: () =>
        API.get(`/dataset-relations/`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createDatasetRelation: (data: unknown) =>
        API.post(`/dataset-relations/`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteDatasetRelation: (relationId: string | number) =>
        API.delete(`/dataset-relations/${relationId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveDataset: (datasetId: string | number) =>
        API.post(`/datasets/${datasetId}/archive`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    unarchiveDataset: (datasetId: string | number) =>
        API.post(`/datasets/${datasetId}/unarchive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    // Samples
    getSampleMessages: (params: unknown) => {
        return API.get(`/samples`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err));
    },
    getSampleOrigins: (params: unknown) => {
        return API.get(`/samples/origins`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err));
    },
    getSampleById: (sampleId: string | number) =>
        API.get(`/samples/${sampleId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createFormulaFromSample: (sampleId: string | number) =>
        API.post(`/samples/${sampleId}/create`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Sample Tags
    getIndustryTags: () =>
        API.get(`/sample-tags/industry`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    getFormulaTemplates: (params: unknown) =>
        API.get(`/formula-templates/`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getFormulaTemplateAlternates: (
        template_id: string | number,
        variant_id: string | number | undefined,
    ) =>
        API.get(
            `/formula-templates/${template_id}/alternates/${variant_id}`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    createFormulaFromTemplate: (id: string | number, data: unknown) =>
        API.post(`/formulas/create/template/${id}`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    // States
    getStates: () =>
        API.get(`/states`, config()).catch((err) => handleAxiosErrors(err)),
    getStateById: (stateId: string | number) =>
        API.get(`/states/${stateId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Tags
    getTags: () =>
        API.get(`/tags`, config()).catch((err) => handleAxiosErrors(err)),
    createTag: (data: unknown) =>
        API.post(`/tags`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Goals
    getTrackingEvents: (params: unknown) =>
        API.get(`/events`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getTrackingEvent: (eventId: string | number) =>
        API.get(`/events/${eventId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getGoals: () =>
        API.get(`/goals`, config()).catch((err) => handleAxiosErrors(err)),
    createGoal: (data: unknown) =>
        API.post(`/goals`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateGoal: (id: string | number, data: unknown) =>
        API.post(`/goals/${id}/update`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteGoal: (id: string | number) =>
        API.delete(`/goals/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    manageEvent: ({
        eventId,
        eventData,
    }: {
        eventId: string | number;
        eventData: unknown;
    }) =>
        API.post(`/events/${eventId}/update`, eventData, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    manageBulkEvent: (eventData: unknown) =>
        API.post(`/events/bulk-update`, eventData, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getEventsConfig: () =>
        API.get(`/events/config`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getEventsStats: () =>
        API.get(`/events/stats`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getCustomEventIcons: () =>
        API.get(`/events/custom/icons`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateEventsConfig: ({ data }: { data: unknown }) =>
        API.post(`/events/config`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Snippets
    getSnippets: () =>
        API.get(`/snippets`, config()).catch((err) => handleAxiosErrors(err)),
    updateSnippets: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/snippets/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteSnippets: (data: unknown) =>
        API.delete(`/snippets`, config(data)).catch((err) =>
            handleAxiosErrors(err),
        ),
    approveSnippets: (data: unknown) =>
        API.post(`/snippets/approve`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    draftSnippets: (data: unknown) =>
        API.post(`/snippets/draft`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // integrations
    fetchEventIntegrationDefinition: () =>
        API.get(`/integrations/definition`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchEventIntegrations: () =>
        API.get(`/v1/integrations`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchEventIntegrationsByProviderName: (integration_name: string) =>
        API.get(`/integrations/provider/${integration_name}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    fetchIntegrations: () =>
        API.get(`/integrations`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchApiKeys: (queryParams: unknown) =>
        API.get(`/api-keys`, {
            params: queryParams,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    fetchLlmContext: () =>
        API.get(`/customer/llm-context`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createIntegration: (data: unknown) =>
        API.post(`/v1/integrations`, data, extendedTimeout(200000)).catch(
            (err) => handleAxiosErrors(err),
        ),
    sendIntegrationRequest: (data: unknown) =>
        API.post(`/v1/integrations/request/other`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    createPushIntegration: (data: unknown) =>
        API.post(`/v1/push-integrations`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updatePushIntegration: (id: string | number, data: unknown) =>
        API.put(`push-integrations/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getPushIntegrations: () =>
        API.get(`/v1/push-integrations`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getPushIntegrationById: (id: string | number) =>
        API.get(`/push-integrations/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getProviderDocsFromReadme: (providerName: string) =>
        API.get(
            `/push-integration/instruction/${providerName}`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    getAttributes: (queryParams: unknown) =>
        API.get(`/attribute-filters/fields`, {
            params: queryParams,
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    updateAttribute: (data: unknown) =>
        API.post(`/attribute-filters/fields/update`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    getAttributesOperators: () =>
        API.get(`/attribute-filters/operators`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getAttributeValues: (attribute_id: string | number, queryParams: unknown) =>
        API.get(`/attribute-filters/fields/${attribute_id}/values`, {
            params: queryParams,
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    // Labels
    getAllLabels: (active = false) =>
        API.get(`/labels`, { params: { active }, ...config() }).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLabels: (variant_type: string) =>
        API.get(`/labels/${variant_type}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    getLabelFormulaMetrics: (labelId: string | number, params: unknown) =>
        API.get(`/metrics/labels/${labelId}/formula-label`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    getFormulaLevelMetrics: (labelId: string | number, params: unknown) =>
        API.get(`/metrics/labels/${labelId}/formula`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelAlternateFormulaMetrics: (
        labelId: string | number,
        params: unknown,
    ) =>
        API.get(`/metrics/labels/${labelId}/formula-alternate-label`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelsCount: () =>
        API.get(`/labels/label-count`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLabelIdDetails: (labelId: string | number) =>
        API.get(`/labels/details/${labelId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLabeledAlternates: (labelId: string | number, params: unknown) =>
        API.get(`/labels/details/${labelId}/alternates`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelledFormulaTriggerCount: (labelId: string | number) =>
        API.get(
            `/labels/details/${labelId}/formulas-trigger-count`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    getLabelRealTimeFormulaMetrics: (
        labelId: string | number,
        params: unknown,
    ) =>
        API.get(`/metrics/labels/${labelId}/real-time-formula`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelIdMetricDataRaw: (labelId: string | number, params: unknown) =>
        API.get(`/metrics/labels/${labelId}/raw`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelIdMetricDataAggregated: (
        labelId: string | number,
        params: unknown,
    ) =>
        API.get(`/metrics/labels/${labelId}/aggregated`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    createLabel: (data: unknown) =>
        API.post(`/labels`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    addLabeltoVariant: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/variant/${id}/label`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateLabel: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/labels/${id}/update`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveLabel: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/labels/${id}/archive`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveLabelV1: ({ id, data }: { id: string | number; data: unknown }) =>
        API.post(`/v1/labels/${id}/archive`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkArchiveLabelsV1: (data: unknown) =>
        API.post(`/v1/labels/bulk-archive`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    unarchiveLabel: (labelId: string | number) =>
        API.post(`/label/${labelId}/unarchive`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkUnarchiveLabels: (data: unknown) =>
        API.post(`/label/bulk-unarchive`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteLabel: (labelId: string | number, componentTypeId: string | number) =>
        API.delete(
            `/labels/${labelId}?component_type_id=${componentTypeId}`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    bulkDeleteLabels: (data: unknown) =>
        API.post(`/labels/bulk-delete`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLabelDetailsByVariantTypes: (params: unknown) =>
        API.get(`/labels/details`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelDetailMetrics: (params: unknown) =>
        API.get(`/metrics/labels/details`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelsAndVariants: (variant_type: string, params: unknown) =>
        API.get(`${API_URL}/labels/${variant_type}/variants`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    // Label V1
    getLabelsV1: (componentTypeId: string) =>
        API.get(`${API_URL}/v1/labels/${componentTypeId}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    getLabelsAndVariantsV1: (component_type_id: string, params: unknown) =>
        API.get(`${API_URL}/v1/labels/${component_type_id}/variants`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getLabelDetailsByComponentTypeIds: (params: unknown) =>
        API.get(`${API_URL}/v1/labels/details`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    // Personalization
    getUnlabeledFormulas: () =>
        API.get(`/formulas/unlabeled`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getUnlabelledVariants: () =>
        API.get(`/v1/variants/unlabelled`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getPersonalizationOpportunityMap: (params: unknown) =>
        API.get(`${API_URL}/v1/personalization-map`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),
    // Personalization V2
    getPersonalizationOpportunityMapV2: (params: unknown) =>
        API.get(`${API_URL}/v2/personalization-map`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),

    getHeroRecommendations: (params: unknown) =>
        API.get(`${API_URL}/label-pair-score`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),

    getContentCoverageMapV2: (params: unknown, data: unknown) =>
        API.post(`${API_URL}/v2/content-coverage`, data, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),

    getFormulaCoverage: (data: unknown) =>
        API.post(`/v2/labels/formula-coverage`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // Metrics
    getMessagingFrequencyMap: () =>
        API.get(`/metrics/messaging-frequency`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getSchedules: () =>
        API.get(`/metrics/scheduling`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getMetricDailyImpact: (params: unknown) =>
        API.get(`/metrics/daily-impact`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    getAudiences: (params: unknown) => {
        return API.get(`/audiences`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch(handleAxiosErrors);
    },

    createAudienceBeta: async ({ payload }: { payload: unknown }) =>
        await fetch(ELIGIBILITY_API_URL, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }),

    patchAudienceBeta: async ({
        payload,
        id: audience_id,
    }: {
        payload: unknown;
        id: string;
    }) =>
        await fetch(`${ELIGIBILITY_API_URL}/${audience_id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        }),

    getAudienceByIdBeta: async ({ id: audience_id }: { id: string }) =>
        await fetch(`${ELIGIBILITY_API_URL}/${audience_id}`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }),

    deleteAudienceByIdBeta: async ({ id: audience_id }: { id: string }) =>
        await fetch(`${ELIGIBILITY_API_URL}/${audience_id}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        }),

    getAudiencesBeta: async () => {
        const res = await fetch(ELIGIBILITY_API_URL, {
            credentials: 'include',
        });

        if (!res.ok) {
            let body;
            try {
                body = await res.text();
            } catch {
                /* ignore */
            }
            throw new Error(
                `HTTP ${res.status} ${res.statusText}${
                    body ? ` — ${body}` : ''
                }`,
            );
        }

        return res.json();
    },

    getAudienceCount: async ({ query }: { query: unknown }) =>
        await fetch(`${DATA_MANAGER_API_URL}/count`, {
            method: 'POST',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query }),
        }),

    getTotalAudienceCount: async () => {
        const response = await fetch(`${DATA_MANAGER_API_URL}/total`, {
            method: 'GET',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch total audience count');
        }

        return response.json();
    },

    getAudiencesDetail: () =>
        API.get(`/audiences/detail`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getRewardFunctions: () =>
        API.get(`/reward-functions`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getAudience: (id: string | number | undefined) =>
        API.get(`/audiences/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getAudienceMetrics: (data: unknown, params: unknown) =>
        API.post(`/audience-estimate`, data, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),
    getAudienceSampleIds: (data: unknown, params: unknown) =>
        API.post(`/audience-estimate/sample-ids`, data, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: 60000,
        }).catch((err) => handleAxiosErrors(err)),
    updateAudience: (audience_id: string | number, data: unknown) =>
        API.post(`/audiences/${audience_id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveAudience: (audience_id: string | number) =>
        API.post(`/audiences/${audience_id}/archive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    deleteAudience: (audience_id: string | number) =>
        API.delete(`/audiences/${audience_id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    unarchiveAudience: (audience_id: string | number) =>
        API.post(`/audiences/${audience_id}/unarchive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    createAudience: (data: unknown) =>
        API.post(`/audiences`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    cloneAudience: (audience_id: string | number, data: unknown) =>
        API.post(`/audiences/${audience_id}/clone`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    getMonitoringChartOptions: (params: unknown) =>
        API.get(`/metrics/monitoring/chart-options`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getMonitoringCharts: (params: unknown) =>
        API.get(`/metrics/monitoring/charts`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getMonitoringChartFilterOptions: (params: unknown) =>
        API.get(`/metrics/monitoring/chart-filter-options`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getWeekInProgress: () =>
        API.get(`/metrics/week-in-progress`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getMetricsInfo: () =>
        API.get(`/metrics/info`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getTopNLabelAlternateMetrics: (
        labelId: string | number,
        topN: number,
        params: unknown,
    ) =>
        API.get(`/metrics/labels/${labelId}/alternates/top/${topN}`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getControlledFormulaMetrics: (params: unknown) =>
        API.get(`/metrics/controlled-messages`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getTopFormulaMetric: (params: unknown) =>
        API.get(`/metrics/formulas/top`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getTopLabelMetric: (params: unknown) =>
        API.get(`/metrics/labels/top`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getTopTriggerMetric: (params: unknown) =>
        API.get(`/metrics/triggers/top`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    getMetricStats: (params: unknown) =>
        API.get(`/metrics/top-stats`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    // contact notification token
    notifyDeveloper: (data: unknown) =>
        API.post(`/notify`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    // compound-triggers
    getCompoundTriggers: () =>
        API.get(`/compound-triggers`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getCompoundTriggerDetail: (id: string | number) =>
        API.get(`/compound-triggers/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateSubTrigger: (
        id: string | number,
        subTriggerId: string | number,
        data: unknown,
    ) =>
        API.post(`/sub-triggers/${subTriggerId}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateCompoundTrigger: (id: string | number, data: unknown) =>
        API.post(`/compound-triggers/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createCompoundTrigger: (data: unknown) =>
        API.post(`/compound-triggers`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteSubTrigger: (id: string | number, subTriggerId: string | number) =>
        API.delete(`/sub-triggers/${subTriggerId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkDeleteSubTrigger: (data: unknown) =>
        API.post(`/sub-triggers/delete`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    bulkDeleteTriggers: (data: unknown) =>
        API.post(`/triggers/delete`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveCompoundTrigger: (id: string | number) =>
        API.post(`/compound-triggers/${id}/archive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    unarchiveCompoundTrigger: (id: string | number) =>
        API.post(`/compound-triggers/${id}/unarchive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    //default frequency limit
    getFrequencyLimits: () =>
        API.get(`/frequency-limits`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getDefaultFrequencyLimits: () =>
        API.get(`/default-frequency-limits`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateFrequencyLimits: (data: unknown) =>
        API.post(`/frequency-limits/update`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateDefaultFrequency: (data: unknown) =>
        API.post(`/default-frequency-limits`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    //Email-template
    fetchEmailTemplate: (templateId: string | number) =>
        API.get(`/email/template/${templateId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    fetchTemplates: () =>
        API.get(`/email/templates`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    loadDemoData: () =>
        API.post(`/demo/load`, {}, config({}, 120000)).catch((err) =>
            handleAxiosErrors(err),
        ),

    // formula sequences
    fetchFormulaSequences: (params: unknown) =>
        API.get(`/formula-sequences`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    formulaSequenceCreate: (data: unknown) =>
        API.post(`/formula-sequences`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    formulaSequenceUpdate: (id: string | number, data: unknown) =>
        API.post(`/formula-sequences/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    formulaSequenceDetail: (sequenceId: string | number) =>
        API.get(`/formula-sequences/${sequenceId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    formulaSequenceTouchpointCreate: (
        sequenceId: string | number,
        data: unknown,
    ) =>
        API.post(
            `/formula-sequences/${sequenceId}/touchpoint`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    formulaSequenceTouchpointUpdate: (
        touchpointId: string | number,
        data: unknown,
    ) =>
        API.post(
            `/formula-sequence-touchpoints/${touchpointId}`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    formulaSequenceTouchpointDelete: (
        sequenceId: string | number,
        touchpointId: string | number,
    ) =>
        API.delete(
            `/formula-sequences/${sequenceId}/touchpoint/${touchpointId}`,
            config(),
        ),
    formulaSequenceApprove: (id: string | number) =>
        API.post(`/formula-sequences/${id}/approve`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    formulaSequenceTouchpointFormulaDelete: (
        touchpointId: string | number,
        formulaId: string | number,
    ) =>
        API.delete(
            `/formula-sequence-touchpoints/${touchpointId}/formulas/${formulaId}`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    formulaSequenceSwitchFormulaTouchpoint: (data: unknown) =>
        API.post(
            `/formula-sequence-touchpoints/switch-touchpoints`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    archiveFormulaSequence: (id: string | number) =>
        API.post(`/formula-sequences/${id}/archive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    draftFormulaSequence: (id: string | number) =>
        API.post(`/formula-sequences/${id}/draft`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchFormulaSequencesStatusCount: () =>
        API.get(`/formula-sequences/category-count`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    formulaSequenceReorderTouchpointsUpdate: (data: unknown) =>
        API.post(
            `/formula-sequence-touchpoints/reorder-touchpoints`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    // custom email-templates
    fetchCustomEmailTemplates: () =>
        API.get(`/custom-email-templates`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    customEmailTemplateDetail: (templateId: string | number) =>
        API.get(`/custom-email-templates/${templateId}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    fetchCustomEmailTemplatesMessages: (templateId: string | number) =>
        API.get(
            `/custom-email-templates/${templateId}/formulas`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    customEmailTemplateCreate: (data: unknown) =>
        API.post(`/custom-email-templates`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    customEmailTemplateUpdate: (templateId: string | number, data: unknown) =>
        API.post(`/custom-email-templates/${templateId}`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    archiveCustomEmailTemplate: (templateId: string | number) =>
        API.post(
            `/custom-email-templates/${templateId}/archive`,
            {},
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    draftCustomEmailTemplate: (templateId: string | number) =>
        API.post(
            `/custom-email-templates/${templateId}/draft`,
            {},
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    approveCustomEmailTemplate: (templateId: string | number) =>
        API.post(
            `/custom-email-templates/${templateId}/approve`,
            {},
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    customEmailTemplateClone: (templateId: string | number, data: unknown) =>
        API.post(
            `/custom-email-templates/${templateId}/clone`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    fetchLiveScheduledUniqueMessageCount: ({
        message_type,
    }: {
        message_type: string;
    }) =>
        API.get(
            `/formulas/${message_type}/live-scheduled/unique-messages-count`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    fetchEventStreamVolume: () =>
        API.get(`/events/streams/volume`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchClusterCount: () =>
        API.get(`/metrics/cluster`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    fetchMessageStateCount: ({
        message_type,
        message_state,
    }: {
        message_type: string;
        message_state: string;
    }) =>
        API.get(
            `/metrics/${message_state}/${message_type}/messages/count`,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    // surfaces
    getAllSurfaces: (params: unknown) =>
        API.get(`/surfaces`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    surfaceCreate: (data: unknown) =>
        API.post(`/surfaces`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    surfaceUpdate: (data: unknown, surfaceId: string | number) =>
        API.patch(`/surfaces/${surfaceId}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchSurface: (surfaceId: string | number) =>
        API.get(`/surfaces/${surfaceId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteSurface: (id: string | number) =>
        API.delete(`/surfaces/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    getSurfaceMetrics: (params: unknown) =>
        API.get(`/v1/metrics/surfaces`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: minutesToMilliseconds(5),
        }).catch((err) => handleAxiosErrors(err)),

    getSurfaceDetailMetrics: (params: unknown, surfaceId: string | number) =>
        API.get(`/v1/metrics/surfaces/${surfaceId}`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
            timeout: minutesToMilliseconds(5),
        }).catch((err) => handleAxiosErrors(err)),

    // surface funnel
    getSurfaceFunnel: (surfaceId: string | number) =>
        API.get(`/surfaces/${surfaceId}/funnel`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    surfaceFunnelCreate: (surface_id: string | number, data: unknown) =>
        API.post(`/surfaces/${surface_id}/funnel/`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    updateSurfaceFunnel: (
        surface_id: string | number,
        payload: {
            stage_id: string | number;
            stage_name?: string;
            event_id?: number;
        },
    ) => {
        const { stage_id, ...data } = payload;
        return API.patch(
            `/surfaces/${surface_id}/funnel/${stage_id}`,
            data,
            config(),
        );
    },
    deleteSurfaceFunnel: (
        surfaceId: string | number,
        stageId: string | number,
    ) =>
        API.delete(`/surfaces/${surfaceId}/funnel/${stageId}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updatedSurfaceFunnelOrder: (
        surfaceId: string | number,
        stageId: string | number,
        data: unknown,
    ) =>
        API.put(
            `/surfaces/${surfaceId}/funnel/${stageId}/update-order`,
            data,
            config(),
        ),

    //formula buttons
    fetchFormulaButtons: (formulaId: string | number, params: unknown) =>
        API.get(`/formulas/${formulaId}/buttons`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    updateOrCreateFormulaButtons: (
        formulaId: string | number,
        data: unknown,
        params: unknown,
    ) =>
        API.put(`/formulas/${formulaId}/buttons`, data, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    deleteFormulaButtons: (formulaId: string | number, params: unknown) =>
        API.delete(`/formulas/${formulaId}/buttons`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    // single test message setting
    getSingleTestMessageSettingUser: (params: unknown) =>
        API.get(`/v1/contact-notification-tokens`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    createSingleTestMessageSettingUser: (data: unknown) =>
        API.post(`/v1/contact-notification-tokens`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    deleteSingleTestMessageSettingUser: (id: string | number) =>
        API.delete(`/v1/contact-notification-tokens/${id}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateSingleTestMessageSettingUser: (id: string | number, data: unknown) =>
        API.post(`/v1/contact-notification-tokens/${id}`, data, config()),
    // group test message user
    getGroupTestMessageUsers: (params: unknown) =>
        API.get(`/contact-notification-token/groups`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),
    createGroupTestMessageUser: (data: unknown) =>
        API.post(`/contact-notification-tokens/groups`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    deleteGroupTestMessageUser: (id: string | number) =>
        API.delete(`/contact-notification-tokens/groups/${id}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateGroupTestMessageUser: (id: string | number, data: unknown) =>
        API.put(
            `/contact-notification-tokens/groups/${id}`,
            data,
            config(),
        ).catch((err) => handleAxiosErrors(err)),

    // component types api
    getComponentTypes: () =>
        API.get(`/component-types`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createComponentType: (data: unknown) =>
        API.post(`/component-types`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getComponentTypeDetail: (componentId: string | number) =>
        API.get(`/component-types/${componentId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateComponentType: (componentId: string | number, data: unknown) =>
        API.put(`/component-types/${componentId}`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    archiveComponentType: (componentId: string | number) =>
        API.put(`/component-types/${componentId}/archive`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    unarchiveComponentType: (componentId: string | number) =>
        API.post(
            `/component-types/${componentId}/unarchive`,
            {},
            config(),
        ).catch((err) => handleAxiosErrors(err)),
    deleteComponentType: (component_type_id: string | number) =>
        API.delete(`/component-types/${component_type_id}`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    // component presets
    getComponentPresets: () =>
        API.get(`/component-presets`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    // languages
    getLanguages: () =>
        API.get('/languages', config()).catch((err) => handleAxiosErrors(err)),

    getLanguageDetail: (languageId: string | number) =>
        API.get(`/languages/${languageId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLanguageAttributes: () =>
        API.get(`/languages/attribute-values`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getLocaleCodes: () =>
        API.get('/languages/locale-codes', config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    createLanguage: (data: unknown) =>
        API.post('/languages', data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateLanguage: (languageId: string | number, data: unknown) =>
        API.put(`/languages/${languageId}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    archiveLanguage: (languageId: string | number) =>
        API.patch(`/languages/${languageId}/archive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    unarchiveLanguage: (languageId: string | number) =>
        API.patch(`/languages/${languageId}/unarchive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    getLanguageMessageContent: (languageId: string | number) =>
        API.get(`/languages/${languageId}/message-contents`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    getLanguageAudienceCount: (languageId: string | number) =>
        API.get(`/languages/${languageId}/audience-count`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    getLanguageAttribute: () =>
        API.get(`/languages/attribute`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateLanguageAttribute: (data: unknown) =>
        API.patch(`/languages/attribute`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    // activity logs
    getActivityLogs: (params: unknown) =>
        API.get(`/activity-logs`, {
            params,
        }).catch((err) => handleAxiosErrors(err)),

    // surface templates
    getSurfaceTemplates: (params: unknown) =>
        API.get(`/surface-templates`, {
            params,
            paramsSerializer: (params) => {
                return qs.stringify(params, { arrayFormat: 'repeat' });
            },
            headers: config().headers,
        }).catch((err) => handleAxiosErrors(err)),

    getCustomSurfaceTemplate: (id: string | number) =>
        API.get(`/surface-templates/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    createCustomSurfaceTemplate: (data: unknown) =>
        API.post(`/surface-templates`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    updateCustomSurfaceTemplate: (id: string | number, data: unknown) =>
        API.put(`/surface-templates/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    archiveCustomSurfaceTemplate: (id: string | number) =>
        API.post(`/surface-templates/${id}/archive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    unarchiveCustomSurfaceTemplate: (id: string | number) =>
        API.post(`/surface-templates/${id}/unarchive`, {}, config()).catch(
            (err) => handleAxiosErrors(err),
        ),

    cloneCustomSurfaceTemplate: (id: string | number) =>
        API.post(`/surface-templates/${id}/clone`, {}, config()).catch((err) =>
            handleAxiosErrors(err),
        ),

    // message content
    addMessageContent: (data: unknown) =>
        API.post(`/message-contents`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchMessageContentByFormulaId: (formulaId: string | number) =>
        API.get(`/formulas/${formulaId}/message-contents`, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    fetchMessageContentById: (id: string | number) =>
        API.get(`/message-contents/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    getMessageContentCount: (id: string | number, data: unknown) =>
        API.post(`/message-contents/${id}/message-count`, data, config()).catch(
            (err) => handleAxiosErrors(err),
        ),
    updateMessageContent: (id: string | number, data: unknown) =>
        API.put(`/message-contents/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    grantEntityPermssion: (data: unknown) =>
        API.post(`/entity-permissions/grant`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    revokeEntityPermssion: (data: unknown) =>
        API.post(`/entity-permissions/revoke`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    // topic management
    topicManagementList: () =>
        API.get(`/topics`, config()).catch((err) => handleAxiosErrors(err)),
    createTopic: (data: unknown) =>
        API.post(`/topics`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchTopic: (topicId: string | number) =>
        API.get(`/topics/${topicId}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    fetchTopiLabels: (topicId: string | number) =>
        API.get(`/topics/${topicId}/labels`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    deleteTopic: (id: string | number) =>
        API.delete(`/topics/${id}`, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
    updateTopic: (id: string | number, data: unknown) =>
        API.patch(`/topics/${id}`, data, config()).catch((err) =>
            handleAxiosErrors(err),
        ),
};

export default api_handlers;
